/**
 * Attaching a figure or a document to a knowledge article (#1744 point 4).
 *
 * ── THE ORDER IS ENFORCED, NOT CHOSEN ───────────────────────────────────────
 * Row first, bytes second, and that is the bucket's rule rather than a
 * convention this file could decide differently. Every private bucket installs
 * the same insert policy through `core.ensure_private_bucket`, and for KB the
 * module's callback `service.kb_asset_object_authorized` admits an `insert`
 * only when
 *
 *   · a `service.kb_asset` row ALREADY EXISTS at that exact object key,
 *   · it is still `pending`, and
 *   · the caller holds WRITE authority on the parent article.
 *
 * So there is no upload-then-register path available even if one were wanted:
 * the bytes have nowhere legal to land until the row names their key.
 *
 * ── THE KEY IS THE SERVER'S ─────────────────────────────────────────────────
 * `service.attach_kb_asset` mints `<article>/<nonce>/<key>.<revision>` and
 * returns it. The nonce is why a key cannot be guessed from a list, and a
 * trigger refuses any row whose key does not begin with its own article id. A
 * client that composed a key would be refused twice over; this one never tries.
 *
 * ── `reused` MUST SHORT-CIRCUIT ─────────────────────────────────────────────
 * Attaching the same `asset_key` with byte-identical content returns the
 * EXISTING row and its existing path with `reused = true`. That row is very
 * likely already `verified`, and the insert arm of the policy is false for a
 * verified row — so putting bytes again would be refused. Worse, it would be
 * refused in a way that reads like a broken upload rather than like "this is
 * already attached". So `reused` returns early and says so.
 *
 * ── AND THE SERVER STILL DECIDES WHAT THE BYTES ARE ─────────────────────────
 * Nothing here claims the file is what it says. The hash and the declared type
 * are the UPLOADER'S ASSERTION; a relay later fetches the stored object, sniffs
 * the media from magic bytes, recomputes the digest and writes a verdict. Until
 * then the asset is `pending` and no reader sees it. The surface must say
 * `pending` rather than "done" — which is why this returns the state rather
 * than a success boolean.
 */
import { putPendingKbAssetObject, sha256Hex } from '@solum/sdk'
import type { SolumClient, AuthenticatedObjectOptions } from '@solum/sdk'
import { FIGURE_ID } from '@solum/ui'

/** What the caller must tell us, and nothing it could get wrong silently. */
export interface KbAttachInput {
  articleId: string
  /** The stable figure id a `{{fig:…}}` marker resolves to, or a document name. */
  assetKey: string
  kind: 'figure' | 'document'
  file: File
  altText?: string
  caption?: string
}

export type KbAttachOutcome =
  | { kind: 'attached'; assetId: string; storagePath: string; revision: number }
  | { kind: 'already-attached'; assetId: string; revision: number }

/**
 * The media the server will accept, mirrored here ONLY to fail early with a
 * sentence a person can act on.
 *
 * `service.assert_kb_asset_media` is the boundary and it refuses SVG, HTML and
 * script outright — an SVG is a document that can carry script, and it would be
 * served as a page rather than as a picture. This list is a courtesy, not a
 * control: a file that slips past it is refused by the door, which is the
 * behaviour that matters. It is deliberately NOT read from the server's
 * `figure_mime_types` setting, because a client that discovered the allowlist
 * would also be telling an attacker what the allowlist is.
 */
const ACCEPTS: Record<KbAttachInput['kind'], readonly string[]> = {
  figure: ['image/png', 'image/jpeg', 'image/webp'],
  document: ['application/pdf'],
}

/**
 * The asset key a marker will name, derived from the FILENAME.
 *
 * ── WHY DERIVED AND NOT ASKED ───────────────────────────────────────────────
 * The key is not an internal identifier — it is the thing an author types
 * inside `{{fig:…}}`, so it has to be something they can predict without
 * consulting a list. A key derived from the file they just chose is that; a
 * generated id is not, and a text box asking for one before the upload starts
 * makes the common case worse to buy the rare one.
 *
 * ── AND THE VOCABULARY IS IMPORTED, NEVER RETYPED ───────────────────────────
 * `FIGURE_ID` is the single client-side source, pinned against SQL's
 * `service.kb_figure_id_pattern()` — service/2026090601 REFUSES TO INSTALL if
 * the CHECK stops containing that exact string. Typing the character class here
 * would be a fourth copy and the first one nothing holds down, and a copy that
 * drifted NARROW would refuse a key the server accepts, with a client-side
 * message about a rule the server does not have.
 *
 * Returns null when nothing usable survives — a filename of only punctuation,
 * or one starting with a dot. The caller says so; it does not invent a name,
 * because a name the author cannot predict is the failure this avoids.
 */
const KEY_OK = new RegExp(`^${FIGURE_ID}$`)

export function assetKeyFromFilename(name: string): string | null {
  const key = name
    .replace(/\.[^.]+$/, '')            // the extension is not part of the key
    /* Decompose, then DROP the combining marks. `NFKD` alone turns é into
       `e` + U+0301, and the mark is outside the vocabulary, so the next
       substitution turned it into a dash: `résumé` became `re-sume-`. Its own
       test found that. */
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-')  // everything outside the vocabulary
    .replace(/^[^A-Za-z0-9]+/, '')      // the first character is constrained too
    .slice(0, 64)
    .replace(/[-.]+$/, '')
  return KEY_OK.test(key) ? key : null
}

export function mediaRefusal(kind: KbAttachInput['kind'], type: string): string | null {
  if (ACCEPTS[kind].includes(type)) return null
  return kind === 'figure'
    ? 'A figure must be a PNG, JPEG or WebP. Markup and script are served as a page rather than as a picture, so the server refuses them.'
    : 'A document must be a PDF.'
}

/**
 * What a surface last said about an attach.
 *
 * `wait` is a first-class tone and not a degraded `good`, because the truthful
 * outcome of a SUCCESSFUL attach is not "done" — it is "pending, and here is
 * what that still means". See `attachSaid`.
 */
export interface AttachSaid { tone: 'good' | 'bad' | 'wait'; text: string }

export type AttachPlan =
  | { go: false; said: AttachSaid }
  | { go: true; assetKey: string; kind: KbAttachInput['kind'] }

/**
 * Everything decided BEFORE a request leaves — pure, so every branch can be
 * asserted without a DOM, a client or a token.
 *
 * The container that used to hold this was four early returns interleaved with
 * `setState`, which is exactly the shape whose branches get tested through the
 * one path that happens to be easy to drive.
 */
export function planAttach(files: File[]): AttachPlan {
  const file = files[0]
  if (!file) return { go: false, said: { tone: 'bad', text: 'No file was chosen.' } }
  /* ONE file at a time, deliberately. A batch has to report a partial outcome
     — three attached, one refused for its media, one for its name — and a
     single live region cannot say that truthfully. Naming the limit beats a
     queue whose failures blur into each other. */
  if (files.length > 1) {
    return { go: false, said: { tone: 'bad', text: 'One file at a time, so each one can be answered for on its own.' } }
  }
  /* The kind is DERIVED, not asked. A PDF is a document and a picture is a
     figure; there is no third answer this door accepts, so a chooser would be
     a question whose right answer is already known. */
  const kind: KbAttachInput['kind'] = file.type === 'application/pdf' ? 'document' : 'figure'
  const refusal = mediaRefusal(kind, file.type)
  if (refusal) return { go: false, said: { tone: 'bad', text: refusal } }
  const assetKey = assetKeyFromFilename(file.name)
  if (!assetKey) {
    return {
      go: false,
      said: {
        tone: 'bad',
        text: `“${file.name}” leaves no usable name. Rename it to start with a letter or a digit — the name becomes the figure id you type inside {{fig:…}}.`,
      },
    }
  }
  return { go: true, assetKey, kind }
}

/**
 * What to say once the server has answered — and the reason this is a named,
 * separately tested function rather than two string literals in a `.then`.
 *
 * A successful attach leaves the asset **pending**. No reader sees the figure,
 * and publication stays blocked, until the relay has read the stored bytes and
 * agreed they are what was declared. A surface answering "Uploaded ✓" would be
 * telling an author their figure is in the article when it is not — and the row
 * that then appears in the list below would contradict the message above it.
 *
 * So the success sentence carries the word `pending` and says what is still
 * true. `kbUpload.test.ts` asserts that it never claims otherwise, which is a
 * property a screenshot review would not have caught.
 */
export function attachSaid(outcome: KbAttachOutcome, assetKey: string): AttachSaid {
  if (outcome.kind === 'already-attached') {
    return {
      tone: 'good',
      text: `${assetKey} already holds exactly these bytes — revision ${outcome.revision}. Nothing was uploaded again.`,
    }
  }
  return {
    tone: 'wait',
    text: `${assetKey} is attached and PENDING. The server is reading those bytes; no reader sees the figure, and publication stays blocked, until it agrees they are what was declared.`,
  }
}

/**
 * Attach one asset. Throws with a sentence the surface can render; the caller
 * decides how to say it.
 */
export async function attachKbAsset(
  client: SolumClient,
  /* The SDK's own options, so this cannot drift from what the transport takes. */
  storage: Omit<AuthenticatedObjectOptions, 'verify' | 'contentType'>,
  input: KbAttachInput,
): Promise<KbAttachOutcome> {
  const refusal = mediaRefusal(input.kind, input.file.type)
  if (refusal) throw new Error(refusal)

  const bytes = await input.file.arrayBuffer()
  // Mandatory, and it REFUSES on a host without WebCrypto rather than sending
  // null — `attach_kb_asset` hard-requires a 64-hex digest and the relay
  // compares the stored bytes against it.
  const sha256 = await sha256Hex(bytes)

  const rows = await client.raw().rpcTable<{
    asset_id: string; storage_path: string; revision: number; reused: boolean
  }>('attach_kb_asset', {
    p_article_id: input.articleId,
    p_asset_key: input.assetKey,
    p_kind: input.kind,
    p_content_type: input.file.type,
    p_size_bytes: input.file.size,
    p_sha256: sha256,
    p_alt_text: input.altText ?? null,
    p_caption: input.caption ?? null,
  }, 'service')

  const row = rows[0]
  if (!row?.storage_path) throw new Error('the server did not name a place to put that file')

  /* Byte-identical to what is already attached. The row came back rather than
     being created, its bytes are already stored and very likely verified, and
     the bucket would refuse a second insert — so stop here and say the true
     thing instead of producing a refusal that reads like a broken upload. */
  if (row.reused) {
    return { kind: 'already-attached', assetId: row.asset_id, revision: row.revision }
  }

  await putPendingKbAssetObject(
    { bucket: 'service-kb-assets', storage_path: row.storage_path }, input.file,
    { ...storage, contentType: input.file.type },
  )

  // Only now does verifier work exist. A fast relay can no longer inspect a
  // server-minted path before this client has put the bytes there (#1965).
  await client.raw().rpcScalar<string>('complete_kb_asset_upload', {
    p_article_id: input.articleId,
    p_asset_id: row.asset_id,
  }, 'service')

  return {
    kind: 'attached', assetId: row.asset_id,
    storagePath: row.storage_path, revision: row.revision,
  }
}
